import { AlertCircle, Send, Square } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FileMention } from "./file-mention";
import type { ClientPhase } from "./session-status";

interface ComposerProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onInterrupt?: () => void;
  clientPhase: ClientPhase;
  sessionActive: boolean; // agent is actively running server-side
  canInterrupt: boolean;
  error: string | null;
  onDismissError: () => void;
  placeholder?: string;
  /** If provided, enables @-mention filename autocomplete. */
  deskId?: string;
}

/**
 * The composer deliberately keeps the message text in local state even after
 * send fails — so the user never loses their prompt. Error messages show
 * inline above the input with a dismissable affordance.
 */
export function SessionComposer({
  value,
  onChange,
  onSend,
  onInterrupt,
  clientPhase,
  sessionActive,
  canInterrupt,
  error,
  onDismissError,
  placeholder,
  deskId,
}: ComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [cursorPos, setCursorPos] = useState(0);
  const [mentionOpen, setMentionOpen] = useState(false);

  const busy = clientPhase !== "idle" || sessionActive;
  const disabled = busy;

  // Auto-size textarea to content (up to 6 lines). We intentionally depend
  // on `value` even though it's not explicitly referenced in the effect
  // body — scrollHeight changes when value changes, and we need to re-run
  // then.
  // biome-ignore lint/correctness/useExhaustiveDependencies: value drives scrollHeight indirectly
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 144)}px`;
  }, [value]);

  const effectivePlaceholder =
    placeholder ??
    (clientPhase === "waking_desk"
      ? "Waking desk…"
      : clientPhase === "sending"
        ? "Sending…"
        : clientPhase === "awaiting_first_event"
          ? "Agent is starting…"
          : sessionActive
            ? "Agent is working… (interrupt to send a follow-up)"
            : "Ask the agent…   (@ to mention a file, ⇧⏎ for newline)");

  // Compute whether a mention trigger (@ or /) is active at the cursor.
  // Relies on deskId being set — if the composer is detached from a desk
  // (shouldn't happen in practice), we skip the dropdown entirely.
  const shouldShowMention = (text: string, pos: number): boolean => {
    if (!deskId) return false;
    const before = text.slice(0, pos);
    const at = before.lastIndexOf("@");
    const slash = before.lastIndexOf("/");
    const atValid = at !== -1 && (at === 0 || before[at - 1] === " ");
    const slashValid =
      slash !== -1 && (slash === 0 || before[slash - 1] === " ");
    return atValid || slashValid;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    const pos = e.target.selectionStart ?? next.length;
    onChange(next);
    setCursorPos(pos);
    setMentionOpen(shouldShowMention(next, pos));
  };

  const handleMentionSelect = (
    replacement: string,
    from: number,
    to: number,
  ) => {
    const before = value.slice(0, from);
    const after = value.slice(to);
    const next = `${before}${replacement}${after}`;
    onChange(next);
    setMentionOpen(false);
    // Restore focus + cursor after the inserted path
    const newPos = before.length + replacement.length;
    setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(newPos, newPos);
      setCursorPos(newPos);
    }, 0);
  };

  return (
    <div
      className="relative"
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const filePath = e.dataTransfer.getData("text/rondel-file-path");
        if (filePath) {
          const clean = filePath.startsWith("/") ? filePath : `/${filePath}`;
          const prefix = value.length > 0 && !value.endsWith(" ") ? " " : "";
          onChange(`${value}${prefix}@${clean} `);
          setTimeout(() => textareaRef.current?.focus(), 0);
        }
      }}
    >
      {error && (
        <div
          className="flex items-start gap-2 px-3 py-2 mb-2 rounded-md"
          style={{
            background: "color-mix(in srgb, var(--err) 6%, transparent)",
            border: "1px solid color-mix(in srgb, var(--err) 20%, transparent)",
          }}
        >
          <AlertCircle
            size={13}
            className="mt-0.5 shrink-0"
            style={{ color: "var(--err)" }}
          />
          <div className="flex-1 min-w-0">
            <p
              className="text-[12px] font-medium"
              style={{ color: "var(--err)" }}
            >
              Send failed
            </p>
            <p
              className="text-[11px] mt-0.5 break-words"
              style={{
                color: "var(--t3)",
                fontFamily: "var(--font-mono)",
              }}
            >
              {error}
            </p>
            <p className="text-[11px] mt-1" style={{ color: "var(--t3)" }}>
              Your message is still here — press send to retry.
            </p>
          </div>
          <button
            type="button"
            onClick={onDismissError}
            className="text-[11px] shrink-0"
            style={{ color: "var(--t3)" }}
          >
            dismiss
          </button>
        </div>
      )}

      <div
        className="flex items-end gap-1.5 px-2 py-2 rounded-lg border relative"
        style={{
          background: "var(--white)",
          borderColor: dragOver ? "var(--accent)" : "var(--border)",
          boxShadow: dragOver
            ? "0 0 0 3px color-mix(in srgb, var(--accent) 10%, transparent)"
            : undefined,
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        }}
      >
        {mentionOpen && deskId && (
          <FileMention
            deskId={deskId}
            inputValue={value}
            cursorPosition={cursorPos}
            onSelect={handleMentionSelect}
            onClose={() => setMentionOpen(false)}
          />
        )}
        {canInterrupt && onInterrupt && (
          <button
            type="button"
            onClick={onInterrupt}
            className="p-1.5 rounded shrink-0"
            style={{ color: "var(--err)" }}
            title="Stop agent"
          >
            <Square size={12} fill="currentColor" />
          </button>
        )}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleInputChange}
          onSelect={(e) =>
            setCursorPos((e.target as HTMLTextAreaElement).selectionStart ?? 0)
          }
          onKeyDown={(e) => {
            // When the mention dropdown is open it handles Enter/Tab/Arrows
            // via its own global keydown listener. We only short-circuit the
            // composer's Enter-to-send here.
            if (mentionOpen && (e.key === "Enter" || e.key === "Tab")) return;
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (!disabled && value.trim()) onSend();
            }
          }}
          placeholder={dragOver ? "Drop to attach file…" : effectivePlaceholder}
          disabled={disabled}
          rows={1}
          className="flex-1 bg-transparent resize-none border-0 focus:outline-none disabled:opacity-40 py-1"
          style={{
            color: "var(--t1)",
            fontSize: "13.5px",
            lineHeight: 1.5,
            fontFamily: "var(--font-body)",
            minHeight: "24px",
            maxHeight: "144px",
          }}
        />
        <button
          type="button"
          onClick={onSend}
          disabled={disabled || !value.trim()}
          className="p-1.5 rounded shrink-0 transition-opacity"
          style={{
            background:
              !disabled && value.trim() ? "var(--accent)" : "transparent",
            color: !disabled && value.trim() ? "var(--white)" : "var(--t4)",
            opacity: !value.trim() || disabled ? 0.45 : 1,
          }}
          title={disabled ? "Busy" : "Send (⏎)"}
        >
          <Send size={13} />
        </button>
      </div>
    </div>
  );
}
